<template>
  <div class="assistant-wrap">
    <button class="assistant-toggle" type="button" @click="toggleChat">
      <vue-feather type="message-circle" size="16" />
      <span>Assistente IA WebReceita</span>
    </button>

    <div v-if="isOpen" class="assistant-card">
      <div class="assistant-header">
        <div>
          <strong>Assistente</strong>
          <small>Analise de receitas emitidas</small>
        </div>
        <button class="assistant-close" type="button" @click="isOpen = false">×</button>
      </div>

      <div ref="messagesBox" class="assistant-messages">
        <div v-for="(msg, index) in messages" :key="`msg-${index}`" class="msg" :class="`msg-${msg.role}`">
          <p class="mb-0">{{ msg.text }}</p>
        </div>
        <div v-if="isLoading" class="msg msg-assistant">
          <p class="mb-0">Consultando API do chatbot...</p>
        </div>
      </div>

      <form class="assistant-input" @submit.prevent="handleSend">
        <input v-model="draft" type="text" placeholder="Pergunte sobre receitas, culturas e tecnicos" />
        <button type="submit" :disabled="!draft.trim() || isLoading">Enviar</button>
      </form>
    </div>
  </div>
</template>

<script>
import chatbotWebreceitaService from '@/src/services/chatbot-webreceita'

export default {
  name: 'ExecutiveChatAssistant',
  props: {
    token: {
      type: String,
      default: '',
    },
    context: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isOpen: false,
      draft: '',
      isLoading: false,
      messages: [
        {
          role: 'assistant',
          text: 'Posso ajudar com analise de receitas emitidas, tecnicos, culturas e desempenho por estado.',
        },
      ],
    }
  },
  methods: {
    toggleChat() {
      this.isOpen = !this.isOpen
      this.$nextTick(this.scrollToBottom)
    },
    scrollToBottom() {
      if (this.$refs.messagesBox) {
        this.$refs.messagesBox.scrollTop = this.$refs.messagesBox.scrollHeight
      }
    },
    extractAnswer(data) {
      if (!data) {
        return ''
      }
      if (typeof data === 'string') {
        return data
      }
      if (data.answer) {
        return data.answer
      }
      if (data.message) {
        return data.message
      }
      if (data.response) {
        return data.response
      }
      if (data.text) {
        return data.text
      }
      return ''
    },
    fallbackAnswer(question) {
      const q = question.toLowerCase()
      if (q.includes('estado')) {
        return `Filtro atual de estado: ${this.context.filters.state || 'Todos'}. Posso detalhar receitas por PR, SC e RS.`
      }
      if (q.includes('cultura') || q.includes('soja') || q.includes('milho') || q.includes('trigo')) {
        return `Com os filtros atuais, as culturas em foco sao: ${this.context.filters.culture || 'Todas'}. Posso abrir comparativo de emissao por cultura.`
      }
      if (q.includes('tecnico')) {
        return `Tecnico selecionado: ${this.context.filters.technician || 'Todos'}. Posso destacar taxa de emissao por visita e tempo medio.`
      }
      return 'Nao recebi resposta da API no momento. Tente novamente ou refine a pergunta para receitas emitidas por estado, cultura ou tecnico.'
    },
    async handleSend() {
      const question = this.draft.trim()
      if (!question) {
        return
      }

      this.messages.push({ role: 'user', text: question })
      this.draft = ''
      this.isLoading = true
      this.$nextTick(this.scrollToBottom)

      try {
        const response = await chatbotWebreceitaService.sendMessage(this.token, question)
        const answer = this.extractAnswer(response.data) || this.fallbackAnswer(question)
        this.messages.push({ role: 'assistant', text: answer })
      } catch (error) {
        this.messages.push({ role: 'assistant', text: this.fallbackAnswer(question) })
      } finally {
        this.isLoading = false
        this.$nextTick(this.scrollToBottom)
      }
    },
  },
}
</script>

<style scoped>
.assistant-wrap {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 1060;
}

.assistant-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  border-radius: 999px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #19885a 0%, #0e5c77 100%);
  color: #fff;
  font-weight: 700;
  box-shadow: 0 10px 24px rgba(7, 35, 52, 0.28);
}

.assistant-card {
  width: 360px;
  max-width: calc(100vw - 24px);
  margin-top: 10px;
  border-radius: 14px;
  border: 1px solid #cdddea;
  background: #fff;
  box-shadow: 0 16px 32px rgba(6, 28, 44, 0.26);
  overflow: hidden;
}

.assistant-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: linear-gradient(130deg, #12384f, #186647);
  color: #fff;
}

.assistant-header small {
  display: block;
  opacity: 0.85;
  font-size: 11px;
}

.assistant-close {
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 22px;
  line-height: 1;
}

.assistant-messages {
  height: 290px;
  overflow-y: auto;
  background: #f6fbff;
  padding: 10px;
}

.msg {
  max-width: 88%;
  margin-bottom: 8px;
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 13px;
}

.msg-user {
  margin-left: auto;
  background: #dff4e8;
  color: #184a34;
}

.msg-assistant {
  margin-right: auto;
  background: #e8f1fa;
  color: #203f56;
}

.assistant-input {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  padding: 10px;
  border-top: 1px solid #dce7f0;
}

.assistant-input input {
  border: 1px solid #cbdbe8;
  border-radius: 9px;
  padding: 8px 10px;
  outline: none;
}

.assistant-input input:focus {
  border-color: #1c8a5b;
  box-shadow: 0 0 0 0.2rem rgba(28, 138, 91, 0.13);
}

.assistant-input button {
  border: 0;
  border-radius: 9px;
  padding: 8px 12px;
  background: #0f5f7f;
  color: #fff;
  font-weight: 700;
}

.assistant-input button:disabled {
  opacity: 0.6;
}

@media (max-width: 768px) {
  .assistant-wrap {
    right: 10px;
    bottom: 10px;
  }

  .assistant-card {
    width: calc(100vw - 20px);
  }
}
</style>
